/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col, FormGroup, FormLabel, Form, Button, ListGroup,
         ListGroupItem } from 'react-bootstrap';
import { updateComponent, post, getFromJson } from '../util/Utils'
import AceEditor from 'react-ace';
import 'brace/theme/github';
import 'brace/ext/searchbox';
import { getRules } from './PmtaConfigMode';
import { AlertLevel } from '../App';
import '../css/config.css';
import PropTypes from 'prop-types';


export const UNSAVED_CHANGES = 'Unsaved changes in file.  Please save or discard the changes before choosing a file to configure.'
export const READ_ONLY = 'This file is read-only.'


/**
 * Configuration page of web monitor.
 */
export default class Config extends Component {
    constructor(props) {
        super(props)
        this.state = {
            search: '',
            availableFiles: []
        }
        this.updateConfig = this.updateConfig.bind(this)
        this.updateSelectedFile = this.updateSelectedFile.bind(this)
        this.updateFileList = this.updateFileList.bind(this)
        this.save = this.save.bind(this)
        this.discard = this.discard.bind(this)
        this.onChangeSearch = this.onChangeSearch.bind(this)
        this.sortFiles = this.sortFiles.bind(this)
        this.aceEditor = React.createRef();
    }


    static propTypes = {
        showError: PropTypes.func.isRequired,
        setRefresh: PropTypes.func.isRequired
    };


    async componentDidMount() {
        this.props.setRefresh(undefined)
        this.pmtaConfigMode = new PmtaConfigMode();
        this.setupAceEditor()
        await this.updateFileList(false)
    }


    async setPreviousConfig() {
        let currentConfig = getFromJson(this.state, ['data', 'data', 'content'])
        this.setState({
            previousConfig: currentConfig
        })
    }


    sortFiles(files) {
        if (files && files.length > 0) {
            files.sort((a, b) => {
                if (a.name === b.name) {
                    return 0;
                }
                // Place the default configuration file first for easy access.
                let regex = new RegExp('(^/etc/pmta/config)$|(.+\\\\config\\.dat)$')
                if (regex.test(a.name)) {
                    return -1;
                }
                if (regex.test(b.name)) {
                    return 1;
                }
                return a.name < b.name ? -1 : 1;
            })
        }
    }


    async updateFileList(showSavedMessage) {
        let update = async () => {
            let files = getFromJson(this.state, ['data', 'data', 'files'])
            let state = {
                showSavedMessage: showSavedMessage
            }
            if (files && files.length > 0) {
                this.sortFiles(files)
                await updateComponent(this, '/configFile', {
                    op: 'read',
                    filename: files[0].name,
                    format: 'json-text'
                }, this.setPreviousConfig)

                state = Object.assign(state, {
                    files: files,
                    selectedFile: files[0].name,
                    availableFiles: files,
                })
            }
            this.setState(state)
            if (showSavedMessage) {
                this.props.showError(this.state.successMessage, AlertLevel.SUCCESS)
            }
        }
        await updateComponent(this, '/configFile', {
            op: 'listconfigfiles'
        }, update)
    }


    setupAceEditor() {
        let editor = getFromJson(this, ['aceEditor', 'current', 'editor'])
        // "current" editor would be null with shallow rendering in tests. 
        // We workaround by skipping it for tests.  We are not testing the
        // highlighting part anyway in the tests, and only rely on the
        // ace editor's built-in capabilities.
        editor && editor.getSession().setMode(this.pmtaConfigMode)
        editor && editor.getSession().setOptions({
            tabSize: 4,
            useSoftTabs: true,
            navigateWithinSoftTabs: true
        });
        if (editor) {
            // Fixes the warning: "Automatically scrolling cursor into view
            // after selection change this will be disabled in the next version
            // set editor.$blockScrolling = Infinity to disable this message".
            editor.$blockScrolling = Infinity
        }
    }


    /**
     * Updates the configuration input field.
     * 
     * @param value
     *      the new value for configuration.
     */
    updateConfig(value) {
        // use JSON path for input field to be the same as where PowerMTA
        // returns the configuration, for consistency, so that we have only a
        // single source of truth for what to show to the user.
        this.setState({
            data: {
                data: {
                    content: value
                },
                CSRFToken: getFromJson(this.state, ['data', 'CSRFToken'])
            },
            showSavedMessage: false 
        })
        this.props.showError(undefined)
    }


    /**
     * Returns if the currently shown configuration file has unsaved changes.
     * 
     * @return
     *      whether the file has unsaved changes or not. 
     */
    hasConfigChanged() {
        let currentConfig = getFromJson(this.state, ['data', 'data', 'content'])
        let previousConfig = getFromJson(this.state, ['previousConfig'])
        return currentConfig !== previousConfig
    }


    /**
     * Updates the selected file.  If there are any unsaved changes in the
     * currently shown file, then this method does not update the selection and
     * instead shows an error to alert the user so as to prevent (accidentally)
     * losing such changes.
     * 
     * @param event
     *      event triggered by changing the selection in the select input.
     */
    async updateSelectedFile(event) {
        if (this.hasConfigChanged()) {
            this.props.showError(UNSAVED_CHANGES)
            return
        }

        let file = event.target.value
        this.setState({
            selectedFile: file,
            showSavedMessage: false
        })
        await updateComponent(this, '/configFile', {
            op: 'read',
            filename: file,
            format: 'json-text'
        }, this.setPreviousConfig)
        let editor = getFromJson(this, ['aceEditor', 'current', 'editor'])
        if (editor) {
            // Reset the undo manager so that undoing does not bring up a
            // previously selected file's contents.
            editor.getSession().getUndoManager().reset()
            editor.getSession().setUndoManager(editor.getSession().getUndoManager())
        }
        if (this.isReadOnly(file)) {
            this.props.showError(READ_ONLY, AlertLevel.INFO)
        }
    }


    /**
     * Saves configuration on PowerMTA.
     * 
     * @param event
     *      the event triggered by submitting the form.
     */
    async save(event) {
        event.preventDefault()
        await post(this, '/configFile', {
            'op': 'commit',
            'file.1.name': this.state.selectedFile,
            'file.1.op': 'write',
            'file.1.content': getFromJson(this.state, ['data', 'data', 'content'])
        }, false, async () => {
            // @release 5.0 Should we instruct users about changes to configuration
            // that need a restart?  Detecting that might get tricky as the UI
            // may need to parse the configuration.  It might be simpler if PMTA
            // returned this information through the JSON API somehow.
            await post(this, '/command', {
                command: 'reload',
                format: 'json-text'
            }, false, async () => {
                await this.updateFileList(true)
            })
        })
        this.setState({
            search: '',
            files: this.state.availableFiles
        })
        await this.setPreviousConfig()
    }


    /**
     * Discards unsaved changes made to the currently shown configuration file.
     */
    async discard() {
        let previousConfig = getFromJson(this.state, ['previousConfig'])
        this.setState({
            data: {
                data: {
                    content: previousConfig
                },
                CSRFToken: getFromJson(this.state, ['data', 'CSRFToken'])
            }
        })
        this.props.showError(undefined)
    }


    makeFileOptions() {
        const files = getFromJson(this.state, ['files'], [])
        return files.map((f) => {
            let isActive = this.state.selectedFile === f.name
            return (
                <ListGroupItem action onClick={this.updateSelectedFile}
                               key={f.name} value={f.name} active={isActive}
                               disabled={this.hasConfigChanged()}>
                    {f.name}
                </ListGroupItem>
            )
        })
    }


    isReadOnly(file) {
        // On Linux, config-defaults is always at /etc/pmta/config-defaults.
        // On Windows, config-defaults.dat is at the directory chosen during
        // installation.  The regex below may flag a similarly named file
        // elsewhere in the file system as read-only.  We do not guard against
        // this as it shouldn't be occurring commonly and it introduces
        // unnecessary complexity to the code.
        let regex = new RegExp('(^/etc/pmta/config-defaults)$|' +
                               '(.+\\\\config-defaults.dat)$')
        return regex.test(file)
    }


    async onChangeSearch(event) {
        let files = this.state.availableFiles || []
        this.setState({
            files: files.filter(f => f.name.indexOf(event.target.value) > -1),
            search: event.target.value
        })
    }


    render() {
        let config = getFromJson(this.state, ['data', 'data', 'content'])
        let isReadOnly = this.isReadOnly(this.state.selectedFile) 
        return (
            <Container>
                <Row>
                    <Col md={12}>
                        <FormLabel className='configHeading'>
                            Configuration
                        </FormLabel>
                    </Col>
                </Row>
                <Row>
                    <Col md={3}>
                        <Form.Control type="text" className="searchInput"
                                      placeholder="search for a file"
                                      value={this.state.search}
                                      onChange={this.onChangeSearch}/>
                        <ListGroup className='configFiles'>
                            {this.makeFileOptions()}
                        </ListGroup>
                    </Col>
                    <Col md={9} className='configDiv'>
                        <div className='center'>
                            <Form onSubmit={this.save}>
                                <FormGroup>
                                <AceEditor
                                    ref={this.aceEditor}
                                    mode="text"
                                    theme="github"
                                    onChange={this.updateConfig}
                                    className='config'
                                    showPrintMargin={false}
                                    width='100%'
                                    height='75vh'
                                    minLines={30}
                                    value={config}
                                    readOnly={isReadOnly}/>
                                </FormGroup>
                                {
                                // buttons are enabled only if there are any
                                // unsaved changes.  This is done mainly to
                                // prevent multiple simultaneous clicks, which
                                // on rare ocassions could result in incorrect
                                // behavior, given the asynchronous nature of
                                // callbacks.  Moreover without having any
                                // changes to the file, save and discard 
                                // are no-op anyway.
                                }
                                <FormGroup className="center">
                                    { 
                                    <Button bsPrefix='button actionButton'
                                            type='submit'
                                            disabled={!this.hasConfigChanged()}>
                                        Save
                                    </Button>
                                    }
                                    {
                                    <Button bsPrefix='button actionButton'
                                            onClick={this.discard}
                                            disabled={!this.hasConfigChanged()}>
                                        Discard
                                    </Button>
                                    }
                                </FormGroup>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }
}


/**
 * An Ace "mode" for describing syntax-highlighting rules for PowerMTA
 * configuration.
 * @see https://ace.c9.io/#nav=higlighter&api=virtual_renderer
 */
class PmtaConfigMode extends
    window.ace.acequire('ace/mode/text').Mode {
    constructor() {
        super();
        this.HighlightRules = HighlightRules;
    }
}


/**
 * Describes syntax-highlighting rules for PowerMTA configuration.
 */
class HighlightRules extends
    window.ace.acequire("ace/mode/text_highlight_rules").TextHighlightRules {
    constructor() {
        super();
        this.$rules = getRules()
    }
}
